'use strict';

const {
	h2, a, div, p, section, span, img, ul, li
} = require('iblokz-snabbdom-helpers');
const {fn} = require('iblokz-data');

const md = require('../../util/md');
const gravatar = require('../../util/gravatar');
const {youtubeId} = require('../comp/media/util');
const _trackList = require('../widgets/media/track-list');
const trackList = _trackList.default || _trackList;
const addressMap = require('../widgets/address-map');
const _rl = require('../../util/resolve-localized');
const {resolveLocalized, DEFAULT_LOCALE} = _rl.default || _rl;

const isVenueProfile = profile =>
	Array.isArray(profile && profile.roles) && profile.roles.indexOf('venue') > -1;

const mediaSrc = item => (item && item.url) || '';

const LINK_ICONS = {
	facebook: 'facebook',
	instagram: 'instagram',
	soundcloud: 'soundcloud',
	mixcloud: 'mixcloud',
	github: 'github',
	bandcamp: 'bandcamp',
	youtube: 'youtube-play',
	website: 'globe',
	other: 'link'
};

const linkTypeFromUrl = (url = '') => {
	if (/soundcloud\.com/i.test(url)) return 'soundcloud';
	if (/mixcloud\.com/i.test(url)) return 'mixcloud';
	if (/github\.com/i.test(url)) return 'github';
	if (/instagram\.com/i.test(url)) return 'instagram';
	if (/facebook\.com|fb\.com/i.test(url)) return 'facebook';
	if (/youtube\.com|youtu\.be/i.test(url)) return 'youtube';
	if (/bandcamp\.com/i.test(url)) return 'bandcamp';
	return 'website';
};

const linkMeta = (link, i18n) => {
	const type = (link && link.type) || linkTypeFromUrl(link && link.url);
	const icon = LINK_ICONS[type] || LINK_ICONS.other;
	const labels = (i18n && i18n.enums && i18n.enums.type) || {};
	const label = labels[type] || link.type || type;
	return {type, icon, label};
};

const displayName = (profile, lang) =>
	resolveLocalized(profile && profile.fullName, lang) || '';

const roleLabel = (i18n, role) => {
	if (!role) return '';
	const enums = (i18n && i18n.enums) || {};
	const labels = enums.roles || enums.type || {};
	return labels[role] || role;
};

const rolesEl = (roles, i18n) => {
	const tags = (roles || [])
		.map(r => roleLabel(i18n, r))
		.filter(Boolean)
		.map(label => span('.tag', label));
	return tags.length ? div('.roles', tags) : '';
};

const avatarEl = (profile, size, lang) => fn.pipe(
	() => gravatar.src(profile, size),
	src => src
		? img('.avatar', {attrs: {src, alt: ''}})
		: span('.avatar.placeholder', (displayName(profile, lang) || '?').slice(0, 1))
)();

const videoThumb = (vid) => {
	const yt = youtubeId(vid && vid.url);
	if (yt) {
		return img({
			attrs: {
				src: `https://img.youtube.com/vi/${yt}/hqdefault.jpg`,
				alt: vid.title || ''
			}
		});
	}
	return span('.video-thumb-placeholder');
};

const isListed = profile => !profile.status || profile.status === 'published';

const listView = ({state, actions, i18n}) => {
	const lang = state.lang || DEFAULT_LOCALE;
	// Drafts stay out of Общност but remain open via /profiles/:slug
	const list = (state.profiles.list || []).filter(isListed);
	return section('.profiles.profiles-list', [
		h2('Общност'),
		div([
			list.length === 0
				? p('.profiles-empty', 'Няма публикувани профили засега.')
				: ul('.profile-list', list.map(profile => {
					const path = profile.slug || profile._id;
					return li([
						a(`[href="/profiles/${path}"]`, {
							on: {
								click: ev => {
									ev.preventDefault();
									actions.router.go(`profiles.${path}`);
								}
							}
						}, [
							avatarEl(profile, 96, lang),
							span('.name', displayName(profile, lang) || 'Без име'),
							rolesEl(profile.roles, i18n)
						])
					]);
				}))
		])
	]);
};

/**
 * Profile detail layout:
 *   row 1: profile block | aside (tracks → address → pictures-if-empty-else)
 *   row 2: videos
 *   row 3: pictures (full width when tracks already took the aside)
 */
const picturesSection = ({pictures, i18n}) =>
	section('.profile-pictures', [
		h2((i18n.widget && i18n.widget.pictureList) || 'Снимки'),
		ul('.picture-gallery', pictures.map((src, i) =>
			li('.picture-gallery-item', [
				a('.picture-gallery-link[target="_blank"][rel="noopener"]', {
					attrs: {href: src, title: `photo ${i + 1}`}
				}, [
					img('.picture-gallery-img', {
						attrs: {src, alt: ''}
					})
				])
			])
		))
	]);

const detailView = ({state, actions, i18n}) => {
	const lang = state.lang || DEFAULT_LOCALE;
	const profile = state.profiles.doc || {};
	const name = displayName(profile, lang);
	const about = resolveLocalized(profile.about, lang);
	const links = profile.links || [];
	const tracks = profile.tracks || [];
	const videos = profile.videos || [];
	const pictures = profile.pictures || [];
	const playing = state.profiles.playing || {};
	const trackIndex = typeof playing.track === 'number' && playing.track < tracks.length
		? playing.track
		: (tracks.length ? 0 : -1);
	const showAddress = isVenueProfile(profile)
		&& addressMap.addressHasContent(profile.address);
	const picturesInAside = !tracks.length && pictures.length;
	const aside = [
		tracks.length
			? trackList({
				list: tracks,
				activeIndex: trackIndex,
				paused: playing.paused !== false,
				shuffle: Boolean(playing.shuffle),
				repeat: playing.repeat === true
					? 'all'
					: (playing.repeat || 'off'),
				i18n,
				onSelect: i => actions.set(['profiles', 'playing', 'track'], i),
				onPlayback: (patch) => actions.set(
					['profiles', 'playing'],
					(cur = {}) => Object.assign({}, cur, patch)
				)
			})
			: '',
		showAddress
			? addressMap({address: profile.address, i18n})
			: '',
		picturesInAside
			? picturesSection({pictures, i18n})
			: ''
	].filter(Boolean);

	const goProfiles = ev => {
		ev.preventDefault();
		actions.router.go('profiles');
	};

	return section('.profiles.profile-detail', [
		div('.profile-layout', [
			div('.profile-row.profile-row-main', [
				section('.profile-block', [
					div('.profile-block-header', [
						div('.profile-block-heading', [
							h2('.profile-block-name', [
								a('.profile-name-path[href="/profiles"]', {
									on: {click: goProfiles}
								}, 'Общност'),
								span('.profile-name-sep', ' / '),
								span('.profile-name-current', name || 'Профил')
							]),
							rolesEl(profile.roles, i18n)
						]),
						links.length
							? ul('.profile-links', links.map(link => {
								const {icon, label} = linkMeta(link, i18n);
								return li([
									a('.profile-link[target="_blank"][rel="noopener"]', {
										attrs: {
											href: link.url,
											title: label
										}
									}, [
										span(`.profile-link-icon.fa.fa-${icon}`),
										span('.profile-link-label', label)
									])
								]);
							}))
							: ''
					]),
					div('.about', [
						avatarEl(profile, 280, lang),
						about
							? div('.about-body', {
								props: {innerHTML: md.toDisplayHTML(about)}
							})
							: ''
					])
				]),
				aside.length
					? div('.profile-aside', aside)
					: ''
			]),
			videos.length
				? div('.profile-row.profile-row-media', [
					section('.profile-videos', [
						h2((i18n.widget && i18n.widget.videoList) || 'Видео'),
						ul('.video-gallery', videos.map(vid => {
							const href = mediaSrc(vid);
							const title = vid.title || href || 'video';
							return li('.video-gallery-item', [
								a('.video-gallery-link[target="_blank"][rel="noopener"]', {
									attrs: {href, title}
								}, [
									span('.video-gallery-thumb', [
										videoThumb(vid),
										span('.video-gallery-play.fa.fa-external-link')
									]),
									span('.video-gallery-title', title)
								])
							]);
						}))
					])
				])
				: '',
			tracks.length && pictures.length
				? div('.profile-row.profile-row-pictures', [
					picturesSection({pictures, i18n})
				])
				: ''
		])
	]);
};

module.exports = ({state, actions, i18n}) =>
	state.router.path[1]
		? detailView({state, actions, i18n})
		: listView({state, actions, i18n});
